import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, Row, Col, Container, Modal, Button } from 'react-bootstrap';
import { Page } from 'components';
import Header from '../../components/Header/Header';
import Reload from '../../components/Reload/Reload';
import Api from '../../services/Api';
import { HorizontalBar } from 'react-chartjs-2';

const brandColors = {
  lightGreen: 'var(--brand-light-green, #8fad15)',
  darkGreen: 'var(--brand-dark-green, #1c4220)',
  chart: {
    ram: { light: '#1cc88a', dark: '#128d60' },
    disk: { light: '#36b9cc', dark: '#247f88' },
    cpu: { light: '#4e73df', dark: '#2f5abf' },
    temp: { light: '#e74a3b', dark: '#b8372d' }
  }
};

const EquipmentsTelemetria = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await Api.get(`/api/v1/telemetria-equipments/latest`);
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        setError('Falha ao carregar telemetria.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const parseSizeToBytes = (str) => {
    if (!str || typeof str !== 'string') return null;
    const s = str.replace(/,/g, '.').trim();
    const m = s.match(/([0-9]*\.?[0-9]+)\s*([KMGTP]?)(B)?/i);
    if (!m) return null;
    const num = parseFloat(m[1]);
    const unit = (m[2] || '').toUpperCase();
    const pow = { '': 0, K: 1, M: 2, G: 3, T: 4, P: 5 }[unit] ?? 0;
    return num * Math.pow(1024, pow);
  };

  const charts = useMemo(() => {
    if (!items || items.length === 0) return null;

    const labels = items.map((d) => d.serial || d?.sys_info?.hostname || (d?._id || '').slice(0, 6));

    const asNumber = (v) => (typeof v === 'number' && isFinite(v) ? v : null);

    const cpuTemp = items.map((d) => asNumber(d.cpu_temp));
    const ramPct = items.map((d) => {
      const used = asNumber(d?.ram?.used);
      const total = asNumber(d?.ram?.total);
      if (!used || !total) return null;
      return +(100 * (used / total)).toFixed(1);
    });
    const diskPctFree = items.map((d) => {
      const freeB = parseSizeToBytes(d?.disk?.free);
      const totalB = parseSizeToBytes(d?.disk?.total);
      if (!freeB || !totalB) return null;
      return +(100 * (freeB / totalB)).toFixed(1);
    });
    const uptimeHours = items.map((d) => {
      const m = asNumber(d.uptime_minutes);
      return m != null ? +(m / 60).toFixed(1) : null;
    });

    const makeBarData = (label, data, color) => ({
      labels,
      datasets: [
        {
          label,
          data: data.map((v) => (v == null ? null : v)),
          backgroundColor: color,
        },
      ],
    });

    const baseOptions = {
      legend: { display: false },
      tooltips: { mode: 'index', intersect: false },
      scales: {
        xAxes: [
          {
            ticks: { beginAtZero: true },
          },
        ],
        yAxes: [
          {
            ticks: { autoSkip: false },
          },
        ],
      },
    };

    return {
      cpuTemp: {
        data: makeBarData('Temperatura da CPU (°C)', cpuTemp, '#e74a3b'),
        options: baseOptions,
      },
      ramPct: {
        data: makeBarData('RAM usada (%)', ramPct, '#1cc88a'),
        options: { ...baseOptions, scales: { ...baseOptions.scales, xAxes: [{ ...baseOptions.scales.xAxes[0], suggestedMax: 100 }] } },
      },
      diskFree: {
        data: makeBarData('Disco livre (%)', diskPctFree, '#36b9cc'),
        options: { ...baseOptions, scales: { ...baseOptions.scales, xAxes: [{ ...baseOptions.scales.xAxes[0], suggestedMax: 100 }] } },
      },
      uptime: {
        data: makeBarData('Uptime (h)', uptimeHours, '#858796'),
        options: baseOptions,
      },
    };
  }, [items]);

  const [themeVersion, setThemeVersion] = useState(0);
  useEffect(() => {
    const observer = new MutationObserver(() => setThemeVersion(v => v + 1));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const isDarkTheme = document.body.classList.contains('dark-theme');

  // Modal de informações por faixa
  const [modalInfo, setModalInfo] = useState({ show: false, title: '', items: [] });
  const openModal = (title, items=[]) => setModalInfo({ show: true, title, items });
  const closeModal = () => setModalInfo(mi => ({ ...mi, show: false }));

  const downloadModalItems = useCallback(() => {
    if (!modalInfo.items || modalInfo.items.length === 0) return;
    const header = 'equipamento';
    const lines = modalInfo.items.map(it => `"${(it || '').replace(/"/g,'""')}"`);
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:T]/g,'-').slice(0,19);
    a.href = url;
    a.download = (modalInfo.title ? modalInfo.title.replace(/\s+/g,'_').toLowerCase() : 'equipamentos') + '_' + ts + '.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [modalInfo]);

  const createBarGradient = useCallback((ctx, colors) => {
    const gradient = ctx.createLinearGradient(0, 0, 300, 0);
    gradient.addColorStop(0, isDarkTheme ? colors.dark : colors.light);
    gradient.addColorStop(1, isDarkTheme ? `${colors.dark}99` : `${colors.light}99`);
    return gradient;
  }, [isDarkTheme]);

  // Distribuições por faixas (0-10, 11-20, ..., 91-100)
  const ranges = useMemo(() => {
    const mkRanges = () => {
      const out = [];
      let start = 0;
      while (start <= 100) {
        const end = start === 0 ? 10 : start + 9; // 0-10, 11-20, ...
        out.push({ label: `${start}-${end}`, start, end });
        start = start === 0 ? 11 : start + 10;
      }
      return out;
    };

    const list = mkRanges();

    const labels = items.map((d) => d.serial || d?.sys_info?.hostname || (d?._id || '').slice(0, 6));

    const bucketize = (values) => {
      // Inclui start/end/label em cada bucket para uso no modal
      const buckets = list.map(r => ({ count: 0, items: [], start: r.start, end: r.end, label: r.label }));
      values.forEach((val, idx) => {
        if (val == null || !isFinite(val)) return;
        for (let i = 0; i < list.length; i++) {
          const { start, end } = list[i];
          if (val >= start && val <= end) {
            buckets[i].count += 1;
            buckets[i].items.push(labels[idx]);
            break;
          }
        }
      });
      return buckets;
    };

    // Dados de base já calculados em charts
    const ramPctValues = items.map((d) => {
      const used = typeof d?.ram?.used === 'number' ? d.ram.used : null;
      const total = typeof d?.ram?.total === 'number' ? d.ram.total : null;
      if (!used || !total) return null;
      return +(100 * (used / total)).toFixed(1);
    });

    const diskUsedPctValues = items.map((d) => {
      const freeB = parseSizeToBytes(d?.disk?.free);
      const totalB = parseSizeToBytes(d?.disk?.total);
      if (!freeB || !totalB) return null;
      const freePct = 100 * (freeB / totalB);
      return +(100 - freePct).toFixed(1);
    });

    const cpuUsageValues = items.map((d) => (typeof d.cpu_usage === 'number' ? d.cpu_usage : null));
    const cpuTempValues = items.map((d) => (typeof d.cpu_temp === 'number' ? d.cpu_temp : null));

    const toBarDataset = (label, color, buckets) => ({
      labels: list.map((r) => r.label),
      datasets: [
        {
          label,
          data: buckets.map((b) => b.count),
          backgroundColor: color,
        },
      ],
    });

    const makeBarOptions = (buckets, isTemp = false) => ({
      legend: { display: false },
      tooltips: { enabled: false },
      scales: {
        xAxes: [{ ticks: { autoSkip: false, beginAtZero: true, precision: 0, fontColor: isDarkTheme ? '#adb5bd' : '#666' }, gridLines: { color: isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' } }],
        yAxes: [{ ticks: { reverse: true, fontColor: isDarkTheme ? '#adb5bd' : '#666' }, gridLines: { display: false } }],
      },
      maintainAspectRatio: false,
      responsive: true,
      animation: {
        duration: 400,
        onComplete: function() {
          const chart = this.chart;
          if (!chart) return;
          const ctx = chart.ctx;
          ctx.save();
          ctx.font = 'bold 11px sans-serif';
          ctx.textBaseline = 'middle';
          const baseColor = isDarkTheme ? '#f1f3f5' : '#212529';
          this.data.datasets.forEach((ds, di) => {
            const meta = chart.getDatasetMeta(di);
            if (!meta) return;
            meta.data.forEach((bar, i) => {
              const value = ds.data[i];
              if (value == null || value === 0) return; // não mostra zero
              const model = bar && bar._model; // Chart.js v2
              if (!model) return;
              const padding = 4;
              const text = value.toString();
              ctx.fillStyle = baseColor;
              ctx.textAlign = 'left';
              let x = model.x + padding;
              const y = model.y;
              const chartRight = chart.chartArea && chart.chartArea.right ? chart.chartArea.right : chart.width - 10;
              const textWidth = ctx.measureText(text).width;
              // Se passar do limite, desenha para dentro da barra à direita
              if (x + textWidth > chartRight) {
                ctx.textAlign = 'right';
                x = model.x - padding;
                // Ajusta contraste caso dentro da barra clara/escura
                ctx.fillStyle = isDarkTheme ? '#ffffff' : '#000000';
              }
              ctx.fillText(text, x, y);
            });
          });
          ctx.restore();
        }
      }
    });

    // RAM usada %
    const ramBuckets = bucketize(ramPctValues);
  const ramBar = { data: toBarDataset('RAM usada %', '#1cc88a', ramBuckets), options: makeBarOptions(ramBuckets), buckets: ramBuckets };

    // Disco usado %
    const diskBuckets = bucketize(diskUsedPctValues);
  const diskBar = { data: toBarDataset('Disco usado %', '#36b9cc', diskBuckets), options: makeBarOptions(diskBuckets), buckets: diskBuckets };

    // CPU %
    const cpuBuckets = bucketize(cpuUsageValues);
  const cpuBar = { data: toBarDataset('CPU %', '#4e73df', cpuBuckets), options: makeBarOptions(cpuBuckets), buckets: cpuBuckets };

    // Temperatura CPU (°C) — faixas
    const tempBuckets = bucketize(cpuTempValues);
    const tempBar = {
      data: {
        labels: list.map((r) => r.label),
        datasets: [
          {
            label: 'Temp CPU (°C)',
            data: tempBuckets.map((b) => b.count),
            backgroundColor: '#e74a3b',
          },
        ],
      },
      options: makeBarOptions(tempBuckets, true),
      buckets: tempBuckets,
    };

    return { list, ramBar, diskBar, cpuBar, tempBar };
  }, [items, themeVersion, isDarkTheme]);

  // Handler para clique nas barras
  const enhanceOptionsWithClick = useCallback((baseOptions, buckets, labelPrefix) => {
    if (!baseOptions) return baseOptions;
    return {
      ...baseOptions,
      onClick: (evt, elements) => {
        if (!elements || !elements.length) return;
        const idx = elements[0]._index != null ? elements[0]._index : elements[0].index; // compat v2/v3
        const bucket = buckets && buckets[idx];
        if (!bucket) return;
            let faixa = '';
            if (bucket.start != null && bucket.end != null) {
              faixa = `${bucket.start}-${bucket.end}`;
            } else if (bucket.label) {
              faixa = bucket.label;
            } else {
              faixa = buckets && buckets[idx] && buckets[idx].label ? buckets[idx].label : idx;
            }
            const title = `${labelPrefix} faixa ${faixa}`;
        openModal(title, bucket.items || []);
      },
      hover: { ...baseOptions.hover, onHover: (e, els) => { e.target.style.cursor = els && els.length ? 'pointer' : 'default'; } },
    };
  }, [openModal]);

  return (
    <Page className="p-4" title="TELEMETRIA">
      <Header page="TELEMETRIA" />
      <Container fluid className="px-0">
        <Card className='mb-4 dashboard-card' style={{ borderRadius: '16px' }}>
          <Card.Body>
            <h4 className="fw-bold mb-4 section-header" style={{ color: brandColors.darkGreen }}>Distribuição de Recursos</h4>

            {loading && <Reload />}

            {error && <div className="text-danger">{error}</div>}
            {!loading && !error && charts && ranges && (
              <Row className="g-2">

                <Col xs={12} md={6} lg={6}>
                  <div className="p-3 chart-container h-100" style={{ borderRadius: '12px' }}>
                    <h6 className="fw-bold mb-3" style={{ color: brandColors.darkGreen }}>Disco usado (%) — faixas</h6>
                    <div className="small mb-2" style={{ opacity: .7 }}>Clique em uma barra para ver os equipamentos</div>
                    <div style={{ height: 300 }}>
                      <HorizontalBar
                        data={(canvas) => {
                          const ctx = canvas.getContext('2d');
                          const grad = createBarGradient(ctx, brandColors.chart.disk);
                          return { labels: ranges.diskBar.data.labels, datasets: [{ ...ranges.diskBar.data.datasets[0], backgroundColor: grad, borderColor: isDarkTheme ? brandColors.chart.disk.dark : brandColors.chart.disk.light, borderWidth: 1, hoverBackgroundColor: isDarkTheme ? brandColors.chart.disk.dark : brandColors.chart.disk.light }] };
                        }}
                        options={enhanceOptionsWithClick(ranges.diskBar.options, ranges.diskBar.buckets, 'Disco usado %')}
                      />
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={6}>
                  <div className="p-3 chart-container h-100" style={{ borderRadius: '12px' }}>
                    <h6 className="fw-bold mb-3" style={{ color: brandColors.darkGreen }}>RAM usada (%) — faixas</h6>
                    <div className="small mb-2" style={{ opacity: .7 }}>Clique em uma barra para ver os equipamentos</div>
                    <div style={{ height: 300 }}>
                      <HorizontalBar
                        data={(canvas) => {
                          const ctx = canvas.getContext('2d');
                          const grad = createBarGradient(ctx, brandColors.chart.ram);
                          return { labels: ranges.ramBar.data.labels, datasets: [{ ...ranges.ramBar.data.datasets[0], backgroundColor: grad, borderColor: isDarkTheme ? brandColors.chart.ram.dark : brandColors.chart.ram.light, borderWidth: 1, hoverBackgroundColor: isDarkTheme ? brandColors.chart.ram.dark : brandColors.chart.ram.light }] };
                        }}
                        options={enhanceOptionsWithClick(ranges.ramBar.options, ranges.ramBar.buckets, 'RAM usada %')}
                      />
                    </div>
                  </div>
                </Col>


                <Col xs={12} md={6} lg={6}>
                  <div className="p-3 chart-container h-100" style={{ borderRadius: '12px' }}>
                    <h6 className="fw-bold mb-3" style={{ color: brandColors.darkGreen }}>CPU (%) — faixas</h6>
                    <div className="small mb-2" style={{ opacity: .7 }}>Clique em uma barra para ver os equipamentos</div>
                    <div style={{ height: 300 }}>
                      <HorizontalBar
                        data={(canvas) => {
                          const ctx = canvas.getContext('2d');
                          const grad = createBarGradient(ctx, brandColors.chart.cpu);
                          return { labels: ranges.cpuBar.data.labels, datasets: [{ ...ranges.cpuBar.data.datasets[0], backgroundColor: grad, borderColor: isDarkTheme ? brandColors.chart.cpu.dark : brandColors.chart.cpu.light, borderWidth: 1, hoverBackgroundColor: isDarkTheme ? brandColors.chart.cpu.dark : brandColors.chart.cpu.light }] };
                        }}
                        options={enhanceOptionsWithClick(ranges.cpuBar.options, ranges.cpuBar.buckets, 'CPU %')}
                      />
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={6}>
                  <div className="p-3 chart-container h-100" style={{ borderRadius: '12px' }}>
                    <h6 className="fw-bold mb-3" style={{ color: brandColors.darkGreen }}>Temperatura CPU (°C) — faixas</h6>
                    <div className="small mb-2" style={{ opacity: .7 }}>Clique em uma barra para ver os equipamentos</div>
                    <div style={{ height: 300 }}>
                      <HorizontalBar
                        data={(canvas) => {
                          const ctx = canvas.getContext('2d');
                          const grad = createBarGradient(ctx, brandColors.chart.temp);
                          return { labels: ranges.tempBar.data.labels, datasets: [{ ...ranges.tempBar.data.datasets[0], backgroundColor: grad, borderColor: isDarkTheme ? brandColors.chart.temp.dark : brandColors.chart.temp.light, borderWidth: 1, hoverBackgroundColor: isDarkTheme ? brandColors.chart.temp.dark : brandColors.chart.temp.light }] };
                        }}
                        options={enhanceOptionsWithClick(ranges.tempBar.options, ranges.tempBar.buckets, 'Temp CPU (°C)')}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </Container>
      <Modal
        show={modalInfo.show}
        onHide={closeModal}
        centered
        size="lg"
        contentClassName={isDarkTheme ? 'bg-dark text-light border-secondary' : 'bg-white'}
      >
        <Modal.Header
          closeButton
          className={isDarkTheme ? 'bg-dark text-light border-secondary' : undefined}
          style={{ borderBottom: '1px solid', borderColor: isDarkTheme ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }}
        >
          <Modal.Title className="fw-semibold" style={{ fontSize: '1rem' }}>{modalInfo.title || 'Detalhes'}</Modal.Title>
        </Modal.Header>
        <Modal.Body
          className={isDarkTheme ? 'bg-dark text-light' : undefined}
          style={{ background: isDarkTheme ? '#1f2428' : '#fff' }}
        >
          {(!modalInfo.items || modalInfo.items.length === 0) && (
            <div className={isDarkTheme ? 'text-secondary' : 'text-muted'}>Nenhum equipamento nesta faixa.</div>
          )}
          {modalInfo.items && modalInfo.items.length > 0 && (
            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              <ul className="mb-0" style={{ columns: 2, paddingLeft: '1.1rem', listStyle: 'disc' }}>
                {modalInfo.items.map((name, i) => (
                  <li
                    key={i}
                    style={{ breakInside: 'avoid', padding: '2px 0', fontSize: '.875rem', color: isDarkTheme ? '#e9ecef' : '#212529' }}
                  >
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          className={isDarkTheme ? 'bg-dark text-light border-secondary' : undefined}
          style={{ borderTop: '1px solid', borderColor: isDarkTheme ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }}
        >
          <Button
            variant={isDarkTheme ? 'outline-light' : 'primary'}
            onClick={downloadModalItems}
            disabled={!modalInfo.items || modalInfo.items.length === 0}
          >
            Baixar lista
          </Button>
          <Button variant={isDarkTheme ? 'outline-light' : 'secondary'} onClick={closeModal}>Fechar</Button>
        </Modal.Footer>
      </Modal>
    </Page>
  );
};

export default EquipmentsTelemetria;